(() => {
  const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update.js';

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (!copied) throw new Error('Copy command was rejected.');
  };

  const saveAttribute = async (input) => {
    const name = input.dataset.cartAttribute;
    if (!name) return;
    try {
      const normalizedValue = input.value.trim();
      await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [name]: normalizedValue } }),
      });
      document.querySelectorAll(`[data-cart-attribute="${CSS.escape(name)}"]`).forEach((field) => {
        if (field !== input) field.value = input.value;
      });
    } catch (error) {
      console.warn('Unable to save cart attribute.', error);
    }
  };

  const selectCardSwatch = (swatch) => {
    const card = swatch.closest('.product-card-wrapper');
    const image = card?.querySelector('[data-cathnina-card-image="primary"]');
    const nextSrc = swatch.dataset.imageSrc;
    if (!card || !image || !nextSrc) return;

    card.querySelectorAll('[data-cathnina-swatch]').forEach((button) => {
      const isSelected = button === swatch;
      button.classList.toggle('is-active', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });

    const revealImage = () => image.classList.remove('cathnina-card-image--changing');
    image.classList.add('cathnina-card-image--changing');
    image.addEventListener('load', revealImage, { once: true });
    image.addEventListener('error', revealImage, { once: true });

    if (swatch.dataset.imageSrcset) image.srcset = swatch.dataset.imageSrcset;
    image.src = nextSrc;
    image.alt = swatch.dataset.imageAlt || image.alt;
    card.classList.add('cathnina-card--swatch-selected');

    if (swatch.dataset.variantUrl) {
      card.querySelectorAll('[data-cathnina-card-link]').forEach((link) => {
        link.href = swatch.dataset.variantUrl;
      });
    }
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-attribute]');
    if (input) saveAttribute(input);
  });

  document.addEventListener('click', async (event) => {
    const footerAccordion = event.target.closest?.('.cathnina-footer-accordion');
    if (footerAccordion) {
      const expanded = footerAccordion.getAttribute('aria-expanded') === 'true';
      footerAccordion.setAttribute('aria-expanded', String(!expanded));
      document.getElementById(footerAccordion.getAttribute('aria-controls'))?.classList.toggle('is-open', !expanded);
      return;
    }

    const swatch = event.target.closest?.('[data-cathnina-swatch]');
    if (swatch) {
      event.preventDefault();
      event.stopPropagation();
      selectCardSwatch(swatch);
      return;
    }

    const copyButton = event.target.closest('[data-copy-code]');
    if (copyButton) {
      const code = copyButton.dataset.copyCode?.trim();
      if (!code) return;
      const label = copyButton.querySelector('[data-copy-label]');
      const status = copyButton.parentElement?.querySelector('[data-copy-status]');
      const originalLabel = label?.textContent || 'Copy code';

      try {
        await copyText(code);
        if (label) label.textContent = 'Copied';
        copyButton.classList.add('is-copied');
        if (status) status.textContent = `Discount code ${code} copied.`;

        setTimeout(() => {
          if (label) label.textContent = originalLabel;
          copyButton.classList.remove('is-copied');
          if (status) status.textContent = '';
        }, 1800);
      } catch (error) {
        if (status) status.textContent = `Unable to copy ${code}. Please copy it manually.`;
      }
      return;
    }

    const applyButton = event.target.closest('[data-apply-coupon]');
    if (applyButton) {
      const input = applyButton.closest('.cathnina-coupon-field')?.querySelector('[data-cart-coupon]');
      const code = input?.value.trim();
      if (!code) return;
      await saveAttribute(input);
      const redirect = applyButton.dataset.couponRedirect || '/cart';
      window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
    }
  });

  const syncCartUi = () => {
    const drawer = document.querySelector('cart-drawer');
    const isOpen = drawer?.classList.contains('active') || drawer?.hasAttribute('open');
    const shouldMarkCartOpen = Boolean(isOpen);
    if (document.body.classList.contains('cathnina-cart-open') !== shouldMarkCartOpen) {
      document.body.classList.toggle('cathnina-cart-open', shouldMarkCartOpen);
    }

    const menu = document.querySelector('#menu-drawer');
    const container = document.querySelector('#Details-menu-drawer-container');
    if (menu && container?.open) {
      if (!menu.dataset.cathninaInitialized) {
        menu.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
        menu.dataset.cathninaInitialized = 'true';
      }
    } else if (menu) {
      if (menu.dataset.cathninaInitialized) delete menu.dataset.cathninaInitialized;
    }
  };

  const normalizeReviewLabels = () => {
    document.querySelectorAll('#product-grid .jdgm-widget.jdgm-preview-badge').forEach((widget) => {
      const badge = widget.querySelector('.jdgm-prev-badge');
      const label = widget.querySelector('.jdgm-prev-badge__text, .jdgm-preview-badge__text');
      const rawCount = badge?.dataset.numberOfReviews || label?.textContent.match(/\d[\d,]*/)?.[0] || '';
      const reviewCount = Number(String(rawCount).replace(/,/g, ''));
      const shouldHide = !badge || !rawCount || !Number.isFinite(reviewCount) || reviewCount <= 0;

      if (widget.hidden !== shouldHide) widget.hidden = shouldHide;
      widget.classList.toggle('cathnina-review-badge--empty', shouldHide);
      if (shouldHide || !label) return;

      const expected = `${rawCount} Reviews`;
      if (label.textContent.trim() !== expected) label.textContent = expected;
    });
  };

  const normalizeDisplayedPrices = () => {
    document.querySelectorAll('.money').forEach((price) => {
      const normalized = price.textContent.replace(/\s*USD\s*$/i, '');
      if (price.textContent !== normalized) price.textContent = normalized;
    });
  };

  const initializeCartDatePickers = () => {
    document.querySelectorAll('[data-cart-date-picker]').forEach((picker) => {
      if (picker.dataset.ready === 'true') return;
      picker.dataset.ready = 'true';

      const trigger = picker.querySelector('[data-cart-date-trigger]');
      const calendar = picker.querySelector('[data-cart-calendar]');
      const value = picker.querySelector('[data-cart-date-value]');
      const label = picker.querySelector('[data-cart-date-label]');
      if (!trigger || !calendar || !value || !label) return;

      const minimum = new Date();
      minimum.setHours(12, 0, 0, 0);
      minimum.setDate(minimum.getDate() + Number(picker.dataset.leadDays || 0));

      const parseDate = (raw) => {
        if (!raw) return null;
        const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12);
        const us = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (us) return new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]), 12);
        return null;
      };
      const toIso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const toDisplay = (date) => `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
      const selected = parseDate(value.value);
      let view = new Date((selected || minimum).getFullYear(), (selected || minimum).getMonth(), 1, 12);

      const close = () => {
        calendar.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };

      const render = () => {
        const header = document.createElement('div');
        header.className = 'cathnina-cart-calendar__header';
        const previous = document.createElement('button');
        previous.type = 'button';
        previous.setAttribute('aria-label', 'Previous month');
        previous.textContent = '‹';
        const title = document.createElement('strong');
        title.textContent = view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const next = document.createElement('button');
        next.type = 'button';
        next.setAttribute('aria-label', 'Next month');
        next.textContent = '›';
        header.append(previous, title, next);

        const weekdays = document.createElement('div');
        weekdays.className = 'cathnina-cart-calendar__weekdays';
        ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach((day) => {
          const span = document.createElement('span');
          span.textContent = day;
          weekdays.append(span);
        });

        const days = document.createElement('div');
        days.className = 'cathnina-cart-calendar__days';
        const firstWeekday = new Date(view.getFullYear(), view.getMonth(), 1, 12).getDay();
        const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0, 12).getDate();
        for (let index = 0; index < firstWeekday; index += 1) days.append(document.createElement('span'));
        for (let day = 1; day <= daysInMonth; day += 1) {
          const date = new Date(view.getFullYear(), view.getMonth(), day, 12);
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = String(day);
          button.disabled = date < minimum;
          if (value.value === toIso(date)) button.classList.add('is-selected');
          button.addEventListener('click', async () => {
            value.value = toIso(date);
            label.textContent = toDisplay(date);
            close();
            await saveAttribute(value);
          });
          days.append(button);
        }

        previous.addEventListener('click', () => {
          view = new Date(view.getFullYear(), view.getMonth() - 1, 1, 12);
          render();
        });
        next.addEventListener('click', () => {
          view = new Date(view.getFullYear(), view.getMonth() + 1, 1, 12);
          render();
        });
        calendar.replaceChildren(header, weekdays, days);
      };

      trigger.addEventListener('click', () => {
        const opening = calendar.hidden;
        document.querySelectorAll('[data-cart-calendar]:not([hidden])').forEach((openCalendar) => {
          if (openCalendar !== calendar) openCalendar.hidden = true;
        });
        if (opening) render();
        calendar.hidden = !opening;
        trigger.setAttribute('aria-expanded', String(opening));
      });
    });
  };

  const menuContainer = document.querySelector('#Details-menu-drawer-container');
  menuContainer?.addEventListener('toggle', syncCartUi);

  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('#menu-drawer.cathnina-menu-expanded details > summary');
    if (!summary) return;

    const details = summary.parentElement;
    event.preventDefault();
    event.stopPropagation();
    details.open = !details.open;
    summary.setAttribute('aria-expanded', String(details.open));
  }, true);

  new MutationObserver(() => {
    syncCartUi();
    initializeCartDatePickers();
    normalizeReviewLabels();
    normalizeDisplayedPrices();
  }).observe(document.documentElement, {
    attributes: true,
    childList: true,
    subtree: true,
  });

  const backToTop = document.querySelector('[data-cathnina-back-to-top]');
  backToTop?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));

  if (backToTop) {
    let backToTopFrame;
    let observedChat;
    let chatObserver;

    const updateBackToTopPosition = () => {
      if (window.innerWidth < 750) {
        backToTop.style.removeProperty('left');
        backToTop.style.removeProperty('right');
        backToTop.style.removeProperty('bottom');
        return;
      }

      const chat = document.querySelector('shopify-chat');
      if (chat?.shadowRoot && chat !== observedChat) {
        chatObserver?.disconnect();
        observedChat = chat;
        chatObserver = new MutationObserver(scheduleBackToTopPosition);
        chatObserver.observe(chat.shadowRoot, { attributes: true, childList: true, subtree: true });
      }

      const chatButton = chat?.shadowRoot?.querySelector('[part~="activator"], .activator');
      if (!chatButton) return;
      const chatRect = chatButton.getBoundingClientRect();
      if (!chatRect.width || !chatRect.height) return;

      const buttonSize = backToTop.getBoundingClientRect().width || 52;
      const left = Math.min(
        window.innerWidth - buttonSize - 12,
        Math.max(12, chatRect.left + (chatRect.width - buttonSize) / 2)
      );
      const bottom = Math.max(12, window.innerHeight - chatRect.top + 8);
      backToTop.style.left = `${left.toFixed(2)}px`;
      backToTop.style.right = 'auto';
      backToTop.style.bottom = `${bottom.toFixed(2)}px`;
    };

    function scheduleBackToTopPosition() {
      if (backToTopFrame) return;
      backToTopFrame = requestAnimationFrame(() => {
        backToTopFrame = null;
        updateBackToTopPosition();
      });
    }

    new MutationObserver(scheduleBackToTopPosition).observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('resize', scheduleBackToTopPosition, { passive: true });
    window.visualViewport?.addEventListener('resize', scheduleBackToTopPosition, { passive: true });
    [0, 500, 1500, 4000].forEach((delay) => window.setTimeout(scheduleBackToTopPosition, delay));
  }

  const stickyBar = document.querySelector('[data-cathnina-sticky-atc]');
  const primaryAdd = document.querySelector('[data-cathnina-product] .product-form__submit[name="add"]');
  const buySource = primaryAdd?.closest('.product-form__buttons') || primaryAdd;
  if (stickyBar && buySource && primaryAdd) {
    const syncStickyVisibility = () => {
      const sourceRect = buySource.getBoundingClientRect();
      const show = sourceRect.bottom < 0;
      const header = document.querySelector('.shopify-section-header-sticky, .section-header');
      if (window.innerWidth >= 750 && header) stickyBar.style.top = `${Math.max(0, header.getBoundingClientRect().bottom)}px`;
      stickyBar.classList.toggle('is-visible', show);
      stickyBar.setAttribute('aria-hidden', String(!show));
    };
    document.querySelector('[data-cathnina-sticky-add]')?.addEventListener('click', () => primaryAdd.click());
    window.addEventListener('scroll', syncStickyVisibility, { passive: true });
    syncStickyVisibility();
  }

  const syncScrollControls = () => {
    backToTop?.classList.toggle('is-visible', window.scrollY > 700);
  };
  window.addEventListener('scroll', syncScrollControls, { passive: true });
  initializeCartDatePickers();
  normalizeReviewLabels();
  normalizeDisplayedPrices();
  syncScrollControls();
  syncCartUi();
})();
